import styled, { css, keyframes } from 'styled-components'

import { _S_Medium, mainColors } from '@avantstay/backoffice-core'
import { IconGlitter } from '@avantstay/backoffice-vectors'

import { zIndex } from '@/styles/zIndex'

// Periodic attention pulse: once every 10s three effects fire together within
// the first ~0.8s of the cycle (a sheen flowing through the icon, a blue
// ripple expanding out of the button, and a small size bounce), then the
// button rests for the remainder. All three keyframes share the same duration
// and start on mount, so they stay in sync.
const PULSE_CYCLE = '10s'

// The icon is filled with a white gradient; dipping each stop's opacity in
// sequence (left to right, via staggered delays) reads as the sheen flowing
// through the icon shape.
const iconSheen = keyframes`
  0%, 7%, 100% { stop-opacity: 1; }
  3.5% { stop-opacity: 0.25; }
`

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  8%, 100% { transform: scale(1.2); opacity: 0; }
`

const bounce = keyframes`
  0%, 8%, 100% { transform: scale(1); }
  3% { transform: scale(1.08); }
  5.5% { transform: scale(0.96); }
`

// Zero-sized SVG that only carries the <linearGradient> the icon's fill
// references. It must live in the DOM for url(#ask-remy-icon-sheen) to resolve.
export const SheenDefs = styled.svg`
  position: absolute;
  width: 0;
  height: 0;

  stop {
    animation: ${iconSheen} ${PULSE_CYCLE} ease-in-out infinite;
  }
  stop:nth-of-type(2) {
    animation-delay: 0.12s;
  }
  stop:nth-of-type(3) {
    animation-delay: 0.24s;
  }

  @media (prefers-reduced-motion: reduce) {
    stop {
      animation: none;
    }
  }
`

export const Icon = styled(IconGlitter)`
  flex-shrink: 0;

  path {
    /* With all stops at full opacity this renders as plain white; the sheen
       animation above makes the gradient visible as it sweeps. */
    fill: url(#ask-remy-icon-sheen);
  }
`

// Collapsed the button is a 48px circle showing only the icon; on hover/focus
// it grows into a pill and reveals the label. The label is always in the DOM
// (max-width/opacity animate it) so screen readers announce it either way.
export const Label = styled.span`
  ${_S_Medium};
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
  transition:
    max-width 0.2s ease,
    opacity 0.15s ease,
    margin-left 0.2s ease;
`

// The default corner below is overridden by an inline `style` once the user
// has dragged the button (see useDraggableLauncher). Collapsed size (48px) is
// mirrored there as LAUNCHER_SIZE; keep them in sync.
export const LauncherButton = styled.button<{ $dragging?: boolean }>`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: ${zIndex.ASK_REMY_LAUNCHER};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 14px;
  border: none;
  border-radius: 24px;
  background: ${mainColors.accentDark};
  color: ${mainColors.white};
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  /* Let pointer events drive the drag instead of the browser scrolling/selecting. */
  touch-action: none;
  user-select: none;
  animation: ${bounce} ${PULSE_CYCLE} ease-in-out infinite;
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;

  /* Expanding blue ripple; sits behind the button content and fades out as it
     grows past the button's edge. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    background: ${mainColors.accentMedium};
    opacity: 0;
    animation: ${ripple} ${PULSE_CYCLE} ease-out infinite;
  }

  &:hover,
  &:focus-visible {
    background: ${mainColors.accentExtraDark};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);

    ${Label} {
      max-width: 90px;
      opacity: 1;
      margin-left: 8px;
    }
  }

  /* While dragging, stay a plain 48px circle (the size the viewport clamp
     assumes) and pause the attention pulse so the button tracks the pointer. */
  ${({ $dragging }) =>
    $dragging &&
    css`
      cursor: grabbing;
      animation: none;

      &::before {
        animation: none;
      }

      &:hover ${Label}, &:focus-visible ${Label} {
        max-width: 0;
        opacity: 0;
        margin-left: 0;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    &::before {
      animation: none;
    }
  }
`
